// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ItemFieldSectionData } from '@/items/components/types';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box, styled } from '@mui/material';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';

/**
 * Styled container component for grouping item fields.
 * Provides visual feedback for drag and drop operations.
 */
const Container = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  gridTemplateColumns: 'auto auto 1fr auto',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[100],
  transition: theme.transitions.create('background-color'),
  '&[data-is-over="true"]': {
    backgroundColor: theme.palette.grey[200],
  },
}));

interface Props {
  /**
   * Zero-based index of this section.
   * Used for drag and drop operations to identify the section.
   */
  index: number;

  /**
   * Number of fields in this section.
   * Used to set up the grid template rows for proper layout.
   */
  count: number;
}

/**
 * A container component that groups related item fields into sections.
 * Provides drag and drop functionality for field organization.
 */
export function ItemFieldSection(props: PropsWithChildren<Props>) {
  const ref = useRef<HTMLDivElement>(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  useEffect(() => {
    if (ref.current === null) {
      return;
    }

    const data: ItemFieldSectionData = {
      type: 'section',
      index: props.index,
    };

    return dropTargetForElements({
      element: ref.current,
      getData: () => data,
      getIsSticky: () => true,
      onDragStart: () => setIsDraggedOver(true),
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: () => setIsDraggedOver(false),
    });
  }, [props.index]);

  return (
    <Container
      ref={ref}
      data-is-over={isDraggedOver}
      gridTemplateRows={`repeat(${props.count}, 1fr)`}
    >
      {props.children}
    </Container>
  );
}
